import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { useLang, fmtPrice } from "@/i18n";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { ProductDialog } from "@/components/ProductDialog";
import { Seo } from "@/components/Seo";
import { Pic } from "@/components/Pic";
import { Faq } from "@/components/Faq";
import { HomeGallery } from "@/components/HomeGallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_BG = "/img/hero.jpg";
const COMMUNITY_IMG = "/img/comunidad.jpg";
const SOCIAL_IMG = "/img/protesis_infantil.jpg";
const PROCESS_IMG = "/img/impresion.jpg";
const BOTTLES_IMG = "/img/botellas.jpg";

const CountUp = ({ target, label, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const dur = 1800, start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, target]);
  return (
    <div ref={ref} data-testid={testid}>
      <p className="font-serif-d text-4xl md:text-6xl font-semibold text-[#F7F5F0] leading-none">{n.toLocaleString()}</p>
      <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-[#F7F5F0]/70 mt-2">{label}</p>
    </div>
  );
};

export default function Home() {
  const { t, lang, currency } = useLang();
  const [stats, setStats] = useState({ bottles_recycled: 0, products_created: 0 });
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selected, setSelected] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const featured = products.filter((p) => p.featured).slice(0, 4);
  const chapters = [
    { n: "01", title: t.manifesto.c1t, desc: t.manifesto.c1d, img: BOTTLES_IMG },
    { n: "02", title: t.manifesto.c2t, desc: t.manifesto.c2d, img: null },
    { n: "03", title: t.manifesto.c3t, desc: t.manifesto.c3d, img: PROCESS_IMG },
    { n: "04", title: t.manifesto.c4t, desc: t.manifesto.c4d, img: null },
  ];

  return (
    <div data-testid="home-page">
      <Seo page="home" />
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#132A1D] grain">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <Pic src={HERO_BG} alt="" loading="eager" fetchPriority="high" className="w-full h-[120%] object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#132A1D]/60 via-[#132A1D]/40 to-[#132A1D]" />
        </motion.div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full pt-24 pb-40">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C86A4B] font-bold mb-6">
            {t.hero.kicker}
          </motion.p>
          <h1 data-testid="hero-title" className="font-serif-d font-semibold text-[#F7F5F0] tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl xl:text-[5.5rem]">
            <MaskedLine delay={0.3}>{t.hero.l1}</MaskedLine>
            <MaskedLine delay={0.45}><span className="italic font-medium text-[#F7F5F0]/85">{t.hero.l2}</span></MaskedLine>
            <MaskedLine delay={0.6}><span className="text-[#C86A4B]">{t.hero.l3}</span></MaskedLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="text-base md:text-lg text-[#F7F5F0]/75 max-w-xl mt-8 leading-relaxed">
            {t.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
            className="flex flex-wrap gap-4 mt-10">
            <Link to="/catalogo" data-testid="hero-cta-catalog"
              className="inline-flex items-center gap-2 rounded-full bg-[#C86A4B] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#F7F5F0] hover:text-[#132A1D] transition-colors duration-300">
              {t.hero.cta1} <ArrowRight size={16} />
            </Link>
            <Link to="/disena" data-testid="hero-cta-design"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white px-7 py-3.5 text-sm font-semibold hover:bg-white/10 transition-colors duration-300">
              {t.hero.cta2}
            </Link>
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-0 inset-x-0 z-10 border-t border-white/10 backdrop-blur-sm bg-[#132A1D]/40">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 flex gap-12 md:gap-24">
            <CountUp target={stats.bottles_recycled} label={t.hero.bottles} testid="impact-bottles" />
            <CountUp target={stats.products_created} label={t.hero.products} testid="impact-products" />
          </div>
        </motion.div>
      </section>

      <Marquee />

      {/* MANIFESTO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-40">
        <Reveal>
          <h2 className="font-serif-d text-4xl md:text-6xl font-semibold text-[#132A1D] tracking-tight max-w-2xl mb-20 md:mb-28">
            {t.manifesto.title}
          </h2>
        </Reveal>
        <div className="space-y-16 md:space-y-24">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={0.05 * i}>
              <div className={`grid md:grid-cols-12 gap-6 md:gap-10 items-start border-t border-black/10 pt-8 ${i % 2 === 1 ? "md:text-right" : ""}`}>
                <p className={`md:col-span-2 font-serif-d font-light text-6xl md:text-8xl text-[#2E5A3B]/25 leading-none ${i % 2 === 1 ? "md:order-3" : ""}`}>{c.n}</p>
                <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <h3 className="font-serif-d text-3xl md:text-4xl font-semibold text-[#132A1D] mb-3">{c.title}</h3>
                  <p className={`text-[#5C5C5C] leading-relaxed max-w-md ${i % 2 === 1 ? "md:ml-auto" : ""}`}>{c.desc}</p>
                </div>
                <div className={`md:col-span-4 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                  {c.img && (
                    <div className="overflow-hidden rounded-2xl aspect-[4/3]">
                      <Pic src={c.img} alt={`${c.title}: botellas PET recicladas para impresión 3D`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-[#EFECE6] py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#C86A4B] font-bold mb-3">{t.featured.kicker}</p>
              <h2 className="font-serif-d text-4xl md:text-5xl font-semibold text-[#132A1D] tracking-tight whitespace-pre-line">{t.featured.title}</h2>
            </div>
            <Link to="/catalogo" data-testid="featured-see-all"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#2E5A3B] hover:text-[#C86A4B] transition-colors duration-200">
              {t.featured.all} <ArrowUpRight size={16} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={0.08 * i} className={i === 0 ? "col-span-2 row-span-2" : ""}>
                <a href={`/catalogo/${p.slug.replace(/_/g, "-")}`} data-testid={`featured-product-${p.slug}`}
                  onClick={(e) => { e.preventDefault(); setSelected(p); }} className="group text-left w-full h-full block">
                  <div className={`overflow-hidden rounded-2xl bg-white ${i === 0 ? "aspect-square md:aspect-[1/1.05]" : "aspect-square"}`}>
                    <Pic src={p.image} alt={`${p.name_es} impreso en 3D con material reciclado`}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="flex items-baseline justify-between mt-3 px-1">
                    <h3 className="font-serif-d text-lg md:text-xl font-semibold text-[#132A1D]">{lang === "es" ? p.name_es : p.name_en}</h3>
                    <p className="text-sm font-bold text-[#2E5A3B]">{fmtPrice(p, currency)}</p>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL LINE */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-36">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-3xl aspect-[4/5] md:aspect-[4/4.4]">
              <Pic src={SOCIAL_IMG} alt="Mano de apoyo infantil impresa en 3D, línea social de Pame3D" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-xs uppercase tracking-[0.25em] text-[#C86A4B] font-bold mb-4">{t.social.kicker}</p>
            <h2 className="font-serif-d text-4xl md:text-5xl font-semibold text-[#132A1D] tracking-tight mb-6">{t.social.title}</h2>
            <p className="text-[#5C5C5C] leading-relaxed mb-8 max-w-md">{t.social.desc}</p>
            <Link to="/disena" data-testid="social-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#2E5A3B] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#C86A4B] transition-colors duration-300">
              {t.social.cta} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* REVIEWS / COMMUNITY */}
      <section className="bg-[#EFECE6] py-24 md:py-36 overflow-hidden" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#C86A4B] font-bold mb-3">{t.reviews.kicker}</p>
            <h2 className="font-serif-d text-4xl md:text-5xl font-semibold text-[#132A1D] tracking-tight whitespace-pre-line mb-14">{t.reviews.title}</h2>
          </Reveal>
          <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-start">
            <Reveal className="md:col-span-5">
              <div className="overflow-hidden rounded-3xl aspect-[3/4]">
                <Pic src={COMMUNITY_IMG} alt="Taller educativo de reciclaje e impresión 3D con niños en Barranquilla"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <p className="font-serif-d italic text-lg text-[#5C5C5C] mt-4 px-1">{t.reviews.caption}</p>
            </Reveal>
            <div className="md:col-span-7 space-y-5 md:pt-10">
              {reviews.map((r, i) => (
                <Reveal key={r.id} delay={0.1 * i}>
                  <div data-testid={`review-card-${i}`}
                    className={`bg-white rounded-2xl p-6 md:p-8 border border-black/5 ${i % 2 === 1 ? "md:ml-14" : "md:mr-14"}`}>
                    <p className="font-serif-d italic text-xl md:text-2xl text-[#132A1D] leading-snug mb-4">
                      “{lang === "es" ? r.text_es : r.text_en || r.text_es}”
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-[#2E5A3B] text-white flex items-center justify-center text-xs font-bold">
                        {r.name.charAt(0)}
                      </span>
                      <p className="text-sm font-semibold text-[#132A1D]">{r.name}</p>
                      {r.handle && <p className="text-xs text-[#C86A4B] font-medium">{r.handle}</p>}
                    </div>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.35}>
                <a href="https://instagram.com/pame.3d" target="_blank" rel="noopener noreferrer"
                  data-testid="reviews-instagram-cta"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#2E5A3B] hover:text-[#C86A4B] transition-colors duration-200 mt-2">
                  @pame.3d <ArrowUpRight size={16} />
                </a>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* LOCAL SEO TEXT */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-36" data-testid="local-seo-section">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <Reveal className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-[#C86A4B] font-bold mb-3">{t.seoText.kicker}</p>
            <h2 className="font-serif-d text-4xl md:text-5xl font-semibold text-[#132A1D] tracking-tight">{t.seoText.title}</h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7 space-y-5 text-[#5C5C5C] leading-relaxed text-base md:text-lg">
            <p>{t.seoText.p1}</p>
            <p>{t.seoText.p2}</p>
            <p>{t.seoText.p3}</p>
          </Reveal>
        </div>
      </section>

      <div className="bg-[#EFECE6]"><Faq /></div>

      <HomeGallery />

      {/* PROCESS PREVIEW */}
      <section className="bg-[#132A1D] grain relative py-24 md:py-36 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-8 text-center relative z-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#C86A4B] font-bold mb-4">{t.processPreview.kicker}</p>
            <h2 className="font-serif-d text-4xl md:text-6xl font-semibold text-[#F7F5F0] tracking-tight mb-10">{t.processPreview.title}</h2>
            <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-12 font-serif-d italic text-lg md:text-2xl text-[#F7F5F0]/70">
              {["PET", "→", lang === "es" ? "Tiras" : "Strips", "→", lang === "es" ? "Filamento" : "Filament", "→", lang === "es" ? "Producto" : "Product"].map((s, i) => (
                <span key={i} className={s === "→" ? "text-[#C86A4B]" : ""}>{s}</span>
              ))}
            </div>
            <Link to="/proceso" data-testid="process-preview-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white px-7 py-3.5 text-sm font-semibold hover:bg-white/10 transition-colors duration-300">
              {t.processPreview.cta} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      <ProductDialog product={selected} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  );
}
